import { Entity } from '@api/api/entity-schema';
import { ButtonGroup } from '@design/components/button-group/ButtonGroup';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys } from '@terminal/routes/query-params';
import { CompetitorData } from '@terminal/widgets/knowledge-graph-widget/hooks/useCompetitorsData';
import { SubTabsType } from '@terminal/widgets/theme-widget/ThemeWidget';
import styles from '@terminal/widgets/theme-widget/ThemeWidget.module.scss';
import { useTabsNavigation } from '@terminal/widgets/theme-widget/tabs/hooks/useTabsNavigation';
import { useTranslation } from 'react-i18next';

import { CompetitorDetail } from '../tabs/competitor-detail/CompetitorDetail';
import { CompetitorOverview } from '../tabs/competitor-overview/CompetitorOverview';

type CompetitorsWidgetProps = {
  entity: Entity;
  competitorData: CompetitorData;
};

export const CompetitorsWidget = ({
  entity,
  competitorData,
}: CompetitorsWidgetProps) => {
  const { t } = useTranslation(['widget']);
  const { params } = useQueryParams();

  const subTabs = [
    {
      label: t(
        'widget:assetOverview.knowledgeGraph.competitors.tabs.competitorOverview.title'
      ),
      key: SubTabsType.CompetitorOverview,
    },
    {
      label: t(
        'widget:assetOverview.knowledgeGraph.competitors.tabs.competitorDetail.title'
      ),
      key: SubTabsType.CompetitorDetail,
    },
  ];

  const { activeIndex, onTabChange } = useTabsNavigation(
    subTabs,
    SubTabsType.CompetitorOverview
  );

  const renderContent = () => {
    if (!competitorData) {
      throw new Error('Failed to load CompetitorsWidget');
    }

    if (
      params[queryKeys.activeDrawerTab] === SubTabsType.CompetitorDetail &&
      !!competitorData.entity
    ) {
      return (
        <CompetitorDetail entity={entity} competitor={competitorData.entity} />
      );
    }

    return <CompetitorOverview entity={entity} competitor={competitorData} />;
  };

  return (
    <div className={styles.container}>
      <ButtonGroup
        className={styles.styledButtonGroup}
        labels={subTabs.map(t => t.label)}
        activeIndex={activeIndex}
        onClick={onTabChange}
      />
      <ErrorBoundaryWrapper>{renderContent()}</ErrorBoundaryWrapper>
    </div>
  );
};
